import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useMemo } from 'react';
import { Image, ImageBackground, StyleSheet, Text, View } from 'react-native';

import {
	BlueTable,
	MainUserIcon,
	MainUsersLiveFramePng,
	PlayerGold,
	PlayerGrey,
} from '../../../assets';
import { testProps } from '../../../config';
import { TableItem } from '../../../store/types';
import { useDevice } from '../../../utils/hooks/useDevice';
import { RootStackParamList } from '../../navigator';
import { TouchableOpacity } from '../common/Touchable';

declare global {
	interface Window {
		startGame?: (config: {
			userId: string | number;
			tableId?: string | number;
			width: number;
			height: number;
			ownerId?: string | number;
			tutorial?: boolean;
			isVipRoom?: boolean;
			tableLevel?: number;
			playerName?: string;
			playerAvatarUrl?: string;
			soundEnabled?: boolean;
			translations?: Record<string, any>;
			language?: string;
			drawingBuffer?: boolean;
			payToPlay?: boolean;
			freeToPlay?: boolean;
			payToPlayMinLevel?: number;
			token?: string | null;
			api?: string | undefined;
			eventRunning?: any;
			isEventRunning?: any;
			powerMeter?: any;
			tutorialStartGame?: number;
		}) => void;

		destroyGame?: () => void;
	}
}
interface TournamentTableProps {
	room: TableItem;
	totalCount: number;
	itemIndex: number;
	roomName?: string;
}

type Nav = NativeStackNavigationProp<RootStackParamList>;

function TournamentTable({ room, totalCount, itemIndex, roomName }: TournamentTableProps) {
	const { width, height, isSm, isTablet, isLandscape, isDesktop } = useDevice();
	const isPortrait = (isTablet && height >= width) || (isSm && height >= width);
	const { navigate } = useNavigation<Nav>();

	const headingLabel = useMemo(() => {
		const tierFromServer = room?.tournamentRoomTitle?.trim();
		const tableOrdinal = room?.tournamentTableName?.trim();

		if (tableOrdinal && tierFromServer) {
			return `${tierFromServer} ${tableOrdinal}`;
		}
		if (tableOrdinal && roomName?.trim()) {
			return `${roomName.trim()} ${tableOrdinal}`;
		}
		if (roomName?.trim()) {
			return `${roomName.trim()} ${itemIndex + 1}`;
		}
		return `Table ${itemIndex + 1}`;
	}, [room?.tournamentRoomTitle, room?.tournamentTableName, roomName, itemIndex]);

	console.log(
		'[Tournament] TournamentTable',
		JSON.stringify({
			id: room?.id,
			name: room?.name,
			numberOfSeats: room?.number_of_seats,
			seats: room?.seats,
			busyCount: (room?.seats ?? []).filter((s: any) => s.status !== 'EMPTY').length,
			itemIndex,
			totalCount,
			roomName,
		}),
	);

	const TABLE_ASPECT = 0.65;
	const titleFontSize = 26;
	const titleBottomGap = 30;
	const titleSectionGap = isDesktop ? 14 : 24;
	const badgeSize = 66;
	const badgeTopOffset = isDesktop ? -16 : 0;
	const badgeIconSize = badgeSize * 0.18;
	const badgeIconTop = badgeSize * 0.28;
	const badgeIconLeft = badgeSize * 0.41;

	const isSpecial = isDesktop && itemIndex % 3 === 1 && itemIndex + 2 < totalCount;

	const layout = useMemo(() => {
		const desktopWidth = width * 0.25 - 50;
		const mobileWidth = width * 0.8;

		let imageWidth = mobileWidth * 0.85;
		if (isDesktop) {
			if (isSpecial) {
				imageWidth = desktopWidth * 0.95;
			} else {
				imageWidth = desktopWidth * 0.8;
			}
		}

		return {
			containerWidth: imageWidth,
			containerHeight: imageWidth * TABLE_ASPECT,
		};
	}, [isDesktop, isSpecial, width]);

	const { seats, busyCount } = useMemo(() => {
		const baseDesktop = [
			{ topPercent: -14, leftPercent: 28 },
			{ topPercent: 6, leftPercent: -2 },
			{ topPercent: -14, leftPercent: 58 },
			{ topPercent: 34, leftPercent: -1 },
			{ topPercent: 34, leftPercent: 81 },
			{ topPercent: 6, leftPercent: 81 },
			{ topPercent: 52, leftPercent: 58 },
			{ topPercent: 52, leftPercent: 28 },
		];

		const baseTablet = [
			{ topPercent: -10, leftPercent: 28 },
			{ topPercent: 8, leftPercent: 3 },
			{ topPercent: -10, leftPercent: 58 },
			{ topPercent: 40, leftPercent: 1 },
			{ topPercent: 40, leftPercent: 84 },
			{ topPercent: 6, leftPercent: 82 },
			{ topPercent: 52, leftPercent: 55 },
			{ topPercent: 52, leftPercent: 28 },
		];

		const positions = isDesktop ? baseDesktop : baseTablet;

		const occupiedSeats = (room?.seats ?? []).filter(seat => seat.status !== 'EMPTY');
		const occupiedCount = occupiedSeats.length;

		const occupiedSet = new Set<number>(occupiedSeats.map(s => Number(s.seatNumber)));

		const mapped =
			occupiedSet.size > 0
				? positions.map((pos, idx) => ({
						...pos,
						status: occupiedSet.has(idx + 1) ? 'busy' : 'free',
					}))
				: positions.map((pos, idx) => ({
						...pos,
						status: idx < occupiedCount ? 'busy' : 'free',
					}));

		return { seats: mapped, busyCount: occupiedCount };
	}, [isDesktop, room]);

	// Plain JSX values, not useMemo-created component types: a new component type
	// per dep change forced React to unmount/remount the subtree on every update.
	const sectionOne = (
		<View style={[styles.sectionOneWrap, { marginBottom: titleSectionGap }]}>
			<TouchableOpacity
				onPress={() => navigate('Game', { tableId: room?.id, isTournamentRoom: true })}
				{...testProps(`cta-tournament-table-title-${room?.id}`)}
			>
				<Text
					numberOfLines={1}
					adjustsFontSizeToFit
					minimumFontScale={0.75}
					style={[
						styles.roomTitle,
						{
							fontSize: titleFontSize,
							marginTop: 0,
							marginBottom: titleBottomGap,
							width: layout.containerWidth * 1.35,
							textAlign: 'center',
						},
					]}
				>
					{headingLabel}
				</Text>
			</TouchableOpacity>
		</View>
	);

	let seatSize = 68;
	if (isDesktop && isLandscape) {
		seatSize = 72;
	} else if (isPortrait) {
		seatSize = 76;
	}

	const sectionTwo = (
		<TouchableOpacity
			onPress={() => navigate('Game', { tableId: room?.id, isTournamentRoom: true })}
			style={[styles.container, { width: layout.containerWidth, height: layout.containerHeight }]}
			{...testProps(`cta-tournament-table-open-${room?.id}`)}
		>
			<Image source={BlueTable} style={styles.tableImage} resizeMode='stretch' />
			{seats.map((s, index) => {
				const desktopTopOffset = isDesktop ? 2 : 0;
				const desktopLeftOffset = isDesktop ? 2 : 0;
				const top = Math.round(((s.topPercent + desktopTopOffset) / 100) * layout.containerHeight);
				const left = Math.round(
					((s.leftPercent + desktopLeftOffset) / 100) * layout.containerWidth,
				);
				return (
					<Image
						key={index}
						source={s.status === 'free' ? PlayerGrey : PlayerGold}
						style={[
							styles.seatIcon,
							{
								top,
								left,
								width: seatSize,
								height: seatSize,
							},
						]}
						resizeMode='contain'
					/>
				);
			})}
		</TouchableOpacity>
	);

	const sectionThree = (
		<View style={[styles.participantsWrap, { marginTop: badgeTopOffset }]}>
			<ImageBackground
				source={MainUsersLiveFramePng}
				style={[styles.badgeStyle, { width: badgeSize, height: badgeSize }]}
				resizeMode='contain'
			>
				<Image
					source={MainUserIcon}
					style={[
						styles.badgeUserIcon,
						{
							width: badgeIconSize,
							height: badgeIconSize,
							top: badgeIconTop,
							left: badgeIconLeft,
						},
					]}
					resizeMode='contain'
				/>
				<Text
					style={[styles.textsStyle, { fontSize: badgeSize * 0.18, marginTop: badgeSize * 0.18 }]}
				>
					{busyCount}/{room?.number_of_seats}
				</Text>
			</ImageBackground>
		</View>
	);

	return (
		<View style={[styles.wrapper, { width: isDesktop ? '80%' : '60%' }]}>
			<View style={styles.columnStackBetween}>
				{sectionOne}
				{sectionTwo}
				{sectionThree}
			</View>
		</View>
	);
}

const styles = StyleSheet.create({
	sectionOneWrap: {
		alignItems: 'center',
	},
	columnStackBetween: {
		flexDirection: 'column',
		justifyContent: 'space-between',
		alignItems: 'center',
	},
	wrapper: {
		padding: 10,
		borderRadius: 12,
		backgroundColor: 'transparent',
		alignSelf: 'center',
		margin: 'auto',
	},
	container: {
		position: 'relative',
		alignItems: 'center',
		justifyContent: 'center',
	},
	tableImage: {
		width: '100%',
		height: '100%',
	},
	seatIcon: {
		position: 'absolute',
	},
	tableContainerImage: { resizeMode: 'contain' },
	roomTitle: {
		fontWeight: 'bold',
		color: '#fff',
		textShadowColor: 'rgba(0, 0, 0, 0.7)',
		textShadowOffset: { width: 1, height: 1 },
		textShadowRadius: 2,
		textTransform: 'uppercase',
	},
	participantsWrap: { alignItems: 'center' },
	badgeStyle: { justifyContent: 'center' },
	badgeUserIcon: {
		position: 'absolute',
	},
	textsStyle: {
		color: '#ffe593',
		textAlign: 'center',
		fontWeight: '700',
	},
});

export default TournamentTable;
